import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from 'stores/ToastStore';
import { getSignedUrl, getSignedUrls } from 'utils/DocumentsAPIUtils';

/**
 * Hook that fetches a signed URL on demand and opens it in a new tab.
 *
 * @returns {{ fetchAndOpen: Function, loading: boolean }}
 */
export default function useSignedUrl() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const fetchAndOpen = async ({ documentKey, foreignKey } = {}) => {
    if (!documentKey && !foreignKey) return;

    const newWindow = window.open('', '_blank');
    setLoading(true);
    try {
      let url;
      if (documentKey) {
        const { data, errors } = await getSignedUrl(documentKey);
        if ((errors?.length ?? 0) > 0) {
          showToast(t('GET_SIGNED_URL_ERROR'), errors[0]?.message, 'error');
          newWindow?.close();
          return;
        }
        url = data?.getSignedUrl?.signedUrl;
      } else if (foreignKey) {
        const { data, errors } = await getSignedUrls([foreignKey]);
        if ((errors?.length ?? 0) > 0) {
          showToast(t('GET_SIGNED_URL_ERROR'), errors[0]?.message, 'error');
          newWindow?.close();
          return;
        }
        url = data?.getSignedUrls?.signedUrls?.[0]?.signedUrl;
      }
      if (url) {
        if (newWindow) {
          newWindow.opener = null;
          newWindow.location.href = url;
        }
      } else {
        newWindow?.close();
        showToast(t('GET_SIGNED_URL_ERROR'), null, 'error');
      }
    } catch (e) {
      showToast(t('GET_SIGNED_URL_ERROR'), e.message, 'error');
      newWindow?.close();
    } finally {
      setLoading(false);
    }
  };

  return { fetchAndOpen, loading };
}
